import * as React from 'react';
import { Box } from '@mui/material';

import { Heading, Title } from '../../components/shared/typography/PrimaryTypography';
import { PanelTabs, PanelTabsWrapper } from '../../components/shared/tab/PanelTabsWrapper';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import AccessDenied from '../shared/AccessDenied';
import UploadPanel from '../../components/syntiro_honey/upload/UploadPanel';
import type { ReviewFileSeed } from '../../types/syntiro_honey';
import ReviewPanel from '../../components/syntiro_honey/review/ReviewPanel';
import LearnedRulesPanel from '../../components/syntiro_honey/learned_rules/LearnedRulesPanel';
import AdminPanel from '../../components/syntiro_honey/admin/AdminPanel';
import { UserContext } from '../../context/UserContext';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { ViewAccess, getViewAccess } from '../../util/user_access';

/**
 * Syntiro Honey Data Ingestion view: thin container hosting the Upload and
 * Review steps of the AI-facilitated ESG data ingestion flow. The upload step
 * can hand a just-processed file over to the review step, which then opens
 * pre-filtered to that file's records.
 *
 * Access follows the platform role model via `getViewAccess` (DECISIONS #39):
 * none → AccessDenied; read (Viewer) → Review only, read-only; write/admin
 * (Editor/Admin) → full upload + review + edit + approve.
 * @returns {JSX.Element}
 */
export default function DataIngestion() {
  const [active_tab, setActiveTab] = React.useState(0);
  const [review_seed, setReviewSeed] = React.useState<ReviewFileSeed | null>(null);
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);

  const access: ViewAccess =
    user && group && company
      ? getViewAccess(user, group.id, company.id, 'syntiro_honey.data_ingestion')
      : null;
  if (!user || access === null) {
    // user/group/company hydrate on independent async paths after auth; until the
    // role is known, render neither the editor UI nor a denial (fail closed, the
    // same gate as DataImport and the other role-gated views).
    return <LoadingScreen />;
  }
  if (access === 'none') {
    return <AccessDenied />;
  }
  const read_only = access === 'read';

  return (
    <Box sx={{ px: 6, py: 4 }}>
      <Title weight="Bold" sx={{ mb: 1 }}>
        ESG Data Ingestion
      </Title>
      <Heading weight="Light" sx={{ mb: 4 }}>
        AI-facilitated extraction, mapping and review of ESG documents
      </Heading>
      {read_only ? (
        // Viewers see the review data without any mutation surface — no upload
        // tab (uploading IS a mutation), no edit/approve/confirm affordances.
        <ReviewPanel seed={null} readOnly />
      ) : (
        <PanelTabsWrapper activeTab={active_tab} handleTabChange={setActiveTab}>
          {[
            <PanelTabs key="upload" label="Upload">
              <UploadPanel
                onGoToReview={(seed) => {
                  setReviewSeed(seed ?? null);
                  setActiveTab(1);
                }}
              />
            </PanelTabs>,
            <PanelTabs key="review" label="Review">
              <ReviewPanel seed={review_seed} onSeedDismissed={() => setReviewSeed(null)} />
            </PanelTabs>,
            // The learned-rules management surface (#43): editors teach the rules
            // during review, so editors maintain them here — not admin-gated.
            <PanelTabs key="learned" label="Learned rules">
              <LearnedRulesPanel />
            </PanelTabs>,
            // The Phase-6 management screen — the "all functionality" surface (#39).
            ...(access === 'admin'
              ? [
                  <PanelTabs key="admin" label="Admin">
                    <AdminPanel
                      onReviewFile={(seed) => {
                        setReviewSeed(seed);
                        setActiveTab(1);
                      }}
                    />
                  </PanelTabs>
                ]
              : [])
          ]}
        </PanelTabsWrapper>
      )}
    </Box>
  );
}
